import React, { useState, useEffect } from 'react';

import { fetchClassmatesByCourseId } from '../../services/supabaseClient/courseService';
import LoadingSpinner from './LoadingSpinner';

import './CourseLearners.css';

const CourseLearners = ({ courseId }) => {
  const [coLearners, setCoLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCoLearners = async () => {
      if (!courseId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      // Add minimum delay for smooth transition
      const [{ data, error: fetchError }] = await Promise.all([
        fetchClassmatesByCourseId(courseId),
        new Promise((resolve) => setTimeout(resolve, 500)), // 500ms minimum delay
      ]);

      if (fetchError) {
        setError(fetchError.message || 'Failed to load Coursemates');
        setLoading(false);
        return;
      }

      setCoLearners(data || []);
      setLoading(false);
    };

    loadCoLearners();
  }, [courseId]);

  if (loading) {
    return (
      <div className="course-learners-container">
        <LoadingSpinner message="Loading learners..." size="sm" className="learners-loading" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="course-learners-container">
        <div className="learners-error">Error: {error}</div>
      </div>
    );
  }

  if (coLearners.length === 0) {
    return (
      <div className="course-learners-container">
        <div className="learners-empty">
          <i className="fa-solid fa-users" />
          <p>No coursemates.</p>
        </div>
      </div>
    );
  }

  // Group Coursemates by unique userId to avoid duplicates
  const uniqueCoLearners = coLearners.reduce((acc, coLearner) => {
    if (!acc[coLearner.userId]) {
      // Get display name for this co-learner
      const profile = coLearner.profile;
      let displayName = 'anonymous';

      if (profile) {
        // Try name field first (extract first word), then username, then email
        if (profile.name) {
          // Extract first name from the name field
          displayName = profile.name.trim().split(/\s+/)[0];
        } else if (profile.username) {
          displayName = profile.username;
        } else if (profile.email) {
          displayName = profile.email.split('@')[0];
        }
      }

      acc[coLearner.userId] = {
        ...coLearner,
        displayName, // Add display name to the grouped object
        courses: [],
      };
    }
    acc[coLearner.userId].courses.push({
      courseId: coLearner.courseId,
      courseName: coLearner.courseName,
      progress: coLearner.progress,
      currentLessonIndex: coLearner.currentLessonIndex,
      forkLevel: coLearner.forkLevel,
    });
    return acc;
  }, {});

  const coLearnersList = Object.values(uniqueCoLearners);

  return (
    <div className="course-learners-container">
      <div className="learners-header">
        <h3>
          <i className="fa-solid fa-users" />
        </h3>
        <span className="learners-count">{coLearnersList.length}</span>
      </div>

      <div className="learners-list">
        {coLearnersList.map((coLearner) => {
          // Use pre-computed display name from grouping
          const displayName = coLearner.displayName || 'Anonymous User';
          const avatarLetter = displayName?.charAt(0)?.toUpperCase() || '?';

          return (
            <div key={coLearner.userId} className="learner-card">
              <div className="learner-avatar">{avatarLetter}</div>
              <div className="learner-info">
                <div className="learner-name">{displayName}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CourseLearners;
